import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { getFacturasEmitidas, getFacturasRecibidas } from "../../utils/conciliacionSII";
import { getPlanCobros, getPlanPagos, actualizarCuotaCobro, actualizarCuotaPago } from "../../utils/planCobros";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";

const VincularMovimientoModal = ({ show, onClose, movimiento, onSuccess }) => {
  const { pais } = useAuthContext();
  const { formatAmount } = useCurrency();
  const [tipoVinculacion, setTipoVinculacion] = useState(movimiento?.tipo === "egreso" ? "pago" : "cobro");
  const [facturas, setFacturas] = useState([]);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [cuotas, setCuotas] = useState([]);
  const [cuotaSeleccionada, setCuotaSeleccionada] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(false);

  const monto = Number(movimiento?.monto || 0);
  const esCobro = tipoVinculacion === "cobro";

  useEffect(() => {
    if (!show || !pais) return;
    cargarFacturas();
    setFacturaSeleccionada(null);
    setCuotas([]);
    setCuotaSeleccionada(null);
    setBusqueda("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tipoVinculacion, pais]);

  const cargarFacturas = async () => {
    setLoading(true);
    try {
      const data = esCobro ? await getFacturasEmitidas(pais) : await getFacturasRecibidas(pais);
      setFacturas(data);
    } catch {
      toast.error("Error cargando facturas");
    } finally {
      setLoading(false);
    }
  };

  const handleSeleccionarFactura = async (f) => {
    setFacturaSeleccionada(f);
    setCuotaSeleccionada(null);
    try {
      const plan = esCobro ? await getPlanCobros(f.id) : await getPlanPagos(f.id);
      setCuotas(plan.filter((c) => c.estado === "pendiente"));
    } catch {
      setCuotas([]);
    }
  };

  const handleConfirmar = async () => {
    if (!facturaSeleccionada) return toast.error("Seleccioná una factura");
    setLoading(true);
    try {
      const updateMov = esCobro
        ? { factura_sii_emitida_id: facturaSeleccionada.id }
        : { factura_sii_recibida_id: facturaSeleccionada.id };

      if (cuotaSeleccionada) {
        Object.assign(
          updateMov,
          esCobro ? { plan_cobro_cuota_id: cuotaSeleccionada.id } : { plan_pago_cuota_id: cuotaSeleccionada.id }
        );
      }

      await client.from("movimientos_bancarios").update(updateMov).eq("id", movimiento.id);

      if (esCobro) {
        await client
          .from("facturas_sii_emitidas")
          .update({ facturafechacobro: movimiento.fecha })
          .eq("id", facturaSeleccionada.id);
      }

      if (cuotaSeleccionada) {
        const fechaReal = movimiento.fecha;
        const cambios = esCobro
          ? { estado: "cobrado", fecha_cobro_real: fechaReal, movimiento_bancario_id: movimiento.id }
          : { estado: "pagado", fecha_pago_real: fechaReal, movimiento_bancario_id: movimiento.id };
        await (esCobro
          ? actualizarCuotaCobro(cuotaSeleccionada.id, cambios)
          : actualizarCuotaPago(cuotaSeleccionada.id, cambios));
      } else if (!esCobro) {
        await client
          .from("facturas_sii_recibidas")
          .update({ plazopago: movimiento.fecha })
          .eq("id", facturaSeleccionada.id);
      }

      toast.success("Movimiento vinculado correctamente");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const facturasFiltradas = facturas.filter((f) => {
    if (!busqueda) return true;
    const texto = busqueda.toLowerCase();
    const nombre = esCobro
      ? (f.razon_social_receptor || "").toLowerCase()
      : (f.razon_social_emisor || "").toLowerCase();
    return nombre.includes(texto) || String(f.folio).includes(texto);
  });

  if (!movimiento) return null;

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Vincular movimiento bancario</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: "0.85rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>{movimiento.descripcion_raw}</div>
            <div style={{ color: "#888", fontSize: "0.78rem" }}>{movimiento.fecha}</div>
          </div>
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              color: movimiento.tipo === "ingreso" ? "#27ae60" : "#c0392b",
            }}
          >
            {formatAmount(monto, "CLP")}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 0,
            marginBottom: 16,
            border: "1px solid #dee2e6",
            borderRadius: 6,
            overflow: "hidden",
            width: "fit-content",
          }}
        >
          {["cobro", "pago"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTipoVinculacion(t)}
              style={{
                padding: "6px 20px",
                fontSize: "0.82rem",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
                background: tipoVinculacion === t ? "#2c3e50" : "transparent",
                color: tipoVinculacion === t ? "#fff" : "#666",
              }}
            >
              {t === "cobro" ? "Cobro de cliente" : "Pago a proveedor"}
            </button>
          ))}
        </div>

        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Buscar por nombre o folio..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ marginBottom: 10 }}
        />

        <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Cargando...</div>
          ) : facturasFiltradas.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin facturas</div>
          ) : (
            facturasFiltradas.map((f) => {
              const nombre = esCobro ? f.razon_social_receptor : f.razon_social_emisor;
              const seleccionada = facturaSeleccionada?.id === f.id;
              return (
                <div
                  key={f.id}
                  onClick={() => handleSeleccionarFactura(f)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "0.83rem",
                    borderBottom: "1px solid #f0f0f0",
                    background: seleccionada ? "#e8f4fd" : "transparent",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <span style={{ fontWeight: seleccionada ? 600 : 400 }}>{nombre}</span>
                    <span style={{ marginLeft: 8, color: "#888", fontSize: "0.75rem" }}>
                      Folio {f.folio} · {f.fecha_emision}
                    </span>
                  </div>
                  <div style={{ color: esCobro ? "#27ae60" : "#c0392b", fontWeight: 600 }}>
                    {formatAmount(f.monto_total, "CLP")}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {cuotas.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: "0.82rem", fontWeight: 600, marginBottom: 6 }}>
              Plan de {esCobro ? "cobro" : "pago"} — seleccioná la cuota:
            </div>
            {cuotas.map((c) => (
              <div
                key={c.id}
                onClick={() => setCuotaSeleccionada(c)}
                style={{
                  padding: "6px 12px",
                  cursor: "pointer",
                  fontSize: "0.82rem",
                  borderRadius: 4,
                  marginBottom: 4,
                  border: `1px solid ${cuotaSeleccionada?.id === c.id ? "#2c3e50" : "#dee2e6"}`,
                  background: cuotaSeleccionada?.id === c.id ? "#e8f4fd" : "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>
                  Cuota {c.numero_cuota} · vence {c.fecha_vencimiento}
                </span>
                <span style={{ fontWeight: 600 }}>{formatAmount(c.monto_cuota, "CLP")}</span>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleConfirmar} disabled={loading || !facturaSeleccionada}>
          {loading ? "Vinculando..." : "Confirmar vinculación"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VincularMovimientoModal;
